import { Box, CircularProgress, Grid } from '@mui/material';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { connect, useDispatch } from 'react-redux';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { ErrorAlert } from '../../components/atoms/Alert-message';
import DownloadList from '../../components/PecrList/DownloadList';
import PageHeader from '../../components/shared/PageHeader';
import EmptyTable from '../../components/tables/EmptyTable';
import ListTable from '../../components/tables/ListTable';
import {
  DEFAULT_FILTERED_SITES,
  DEFAULT_FILTERED_STATUS,
  DEFAULT_PECR_LIST_STATUS,
  DEFAULT_SITE_LIST,
} from '../../constants/pecrConstants';
import {
  createGetAllFiltersOptions,
  deleteFilter,
  getPECRList,
  getSiteList,
  insertFilter,
} from '../../queries/listQueries';
import { insertUserFilter } from '../../queries/userQueries';
import { errorNotification } from '../../reducers/handle-api-status/slice';
import { userInfo } from '../../reducers/user-info/slice';
import { ListFilterTableGrid, modernContentSection } from '../../styles';
import { isEmptyObject } from '../../utils';
import MySavedViewModal from '../MySavedViewModal';
import LeftPanel from './LeftPanel';
//List Page Component
const ListPage = props => {
  const {
    apiConfig,
    productFamilyList = [],
    checkUserInfo,
    errorInAPI,
  } = props;
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();
  const bookmarkRef = useRef(false);
  const urlFilterRef = useRef(false);
  const dispatch = useDispatch();

  const { id: getBookmarkedFilterId } = useParams();
  const filterId = getBookmarkedFilterId ?? (checkUserInfo.filterID || 'List');

  const [listData, setListData] = useState([]);
  const [siteListData, setSiteListData] = useState(DEFAULT_SITE_LIST);
  const [statusListData, setStatusListData] = useState(
    DEFAULT_PECR_LIST_STATUS
  );
  const [isLoading, setIsLoading] = useState(true);
  const [openSaveModal, setOpenSaveModal] = useState(false);
  const [searchString, setSearchString] = useState('');
  const [filterName, setFilterName] = useState('');
  const [selectedFilterOption, setSelectedFilterOption] = useState({});
  const [filterProdFamData, setFilterProdFamData] = useState([]);
  const [filterSiteData, setFilterSiteData] = useState(DEFAULT_FILTERED_SITES);
  const [filterStatusData, setFilterStatusData] = useState(
    DEFAULT_FILTERED_STATUS
  );
  const [errorMessage, setErrorMessage] = useState(null);
  // const { siteList, filteredSites, listStatus, filteredListStatus } =
  // useGetSiteAndListStatus();
  const queryClient = useQueryClient();
  const { mutate: fetchList, isPending } = useMutation({
    mutationFn: getPECRList,
    onSuccess: data => {
      setListData(data);
      setIsLoading(false);
    },
    onError: error => {
      console.error('Error fetching PECR list:', error);
      setIsLoading(false);
      dispatch(
        errorNotification({
          hasErrorOccured: true,
          message: t('error_message'),
        })
      );
    },
  });

  const { mutate: updateUserFilter } = useMutation({
    mutationFn: insertUserFilter,
    onSuccess: (_data, variables) => {
      dispatch(userInfo({ filterID: variables.filterId }));
    },
    onError: error => {
      console.error('Error inserting user filter:', error);
      dispatch(
        errorNotification({
          hasErrorOccured: true,
          message: t('error_message'),
        })
      );
    },
  });

  const { data: allFilterData } = useQuery(
    createGetAllFiltersOptions(
      { userId: checkUserInfo?.userID },
      {
        enabled: !!checkUserInfo?.userID,
        staleTime: Infinity,
        cacheTime: Infinity,
      }
    )
  );

  const { mutateAsync: insertNewFilter } = useMutation({
    mutationFn: insertFilter,
    onSuccess: async (data, _variables) => {
      //need to invalidate the query so it refetches the updated filters
      await queryClient.invalidateQueries({
        queryKey: ['all-filters'],
      });

      //get new filter id and nav to it
      const updatedFilters = queryClient.getQueryData([
        'all-filters',
        { userId: checkUserInfo?.userID },
      ]);

      if (updatedFilters && data.insertId) {
        const findSelectedFilter = updatedFilters.find(item => {
          return item.ID.toString() === data.insertId.toString();
        });

        if (findSelectedFilter) {
          setSelectedFilterOption(findSelectedFilter);
          replaceURL(data.insertId);
        }
      }

      setOpenSaveModal(false);
      setFilterName('');
    },
    onError: error => {
      console.error('Error inserting new filter:', error);
      dispatch(
        errorNotification({
          hasErrorOccured: true,
          message: t('error_message'),
        })
      );
    },
  });

  const { mutateAsync: deleteSavedFilter } = useMutation({
    mutationFn: deleteFilter,
    onError: error => {
      console.error('Error deleting saved filter:', error);
      dispatch(
        errorNotification({
          hasErrorOccured: true,
          message: t('error_message'),
        })
      );
    },
  });

  async function setSiteData() {
    try {
      const data = await getSiteList();
      setSiteListData(data);
      const filterData =
        data &&
        data.length > 0 &&
        data.map(siteList => {
          return {
            ...siteList,
            checked: true,
          };
        });
      return filterData;
    } catch (error) {
      console.error('Error fetching site list:', error);
      dispatch(
        errorNotification({
          hasErrorOccured: true,
          message: t('error_message'),
        })
      );
    }
  }

  async function setDefaultData() {
    //sites
    const filteredSites = await setSiteData();
    setFilterSiteData(filteredSites);
  }

  useEffect(() => {
    setDefaultData();
    //eslint-disable-next-line react-hooks/exhaustive-deps
  }, [apiConfig]);

  function filterCheckedItems(arr, key) {
    return arr
      .filter(item => item.checked)
      .map(item => item[key])
      .toString();
  }

  function getURLFilterData() {
    const searchParams = new URLSearchParams(location.search);
    const hasFilterParams =
      searchParams.has('productFilter') ||
      searchParams.has('sitesFilter') ||
      searchParams.has('statusFilter');

    if (!hasFilterParams) {
      return null;
    }

    return {
      productFilter: searchParams.get('productFilter') || '',
      sitesFilter: searchParams.get('sitesFilter') || '',
      statusFilter: searchParams.get('statusFilter') || '',
    };
  }

  function updateURLWithFilters({
    selectedProductFamily = '',
    selectedSites = '',
    selectedStatus = '',
  }) {
    const searchParams = new URLSearchParams(location.search);

    if (selectedProductFamily) {
      searchParams.set('productFilter', selectedProductFamily);
    } else {
      searchParams.delete('productFilter');
    }

    if (selectedSites) {
      searchParams.set('sitesFilter', selectedSites);
    } else {
      searchParams.delete('sitesFilter');
    }

    if (selectedStatus) {
      searchParams.set('statusFilter', selectedStatus);
    } else {
      searchParams.delete('statusFilter');
    }

    navigate(
      {
        pathname: location.pathname,
        search: searchParams.toString() ? `?${searchParams.toString()}` : '',
      },
      { replace: true }
    );
  }

  const hasURLFilterData = !!getURLFilterData();

  //useEffect is getting exceuted for default page
  useEffect(() => {
    if (
      filterId === 'List' &&
      !hasURLFilterData &&
      filterProdFamData.length > 0 &&
      filterStatusData.length > 0 &&
      filterSiteData.length > 0
    ) {
      setIsLoading(true);

      const filterProdFam = filterCheckedItems(filterProdFamData, 'Name');
      const filterSite = filterCheckedItems(filterSiteData, 'SiteName');
      const filterStatus = filterCheckedItems(filterStatusData, 'Status');

      setSelectedFilterOption({
        ProductFilter: filterProdFam,
        SitesFilter: filterSite,
        StatusFilter: filterStatus,
      });

      urlFilterRef.current = true;
      updateURLWithFilters({
        selectedProductFamily: filterProdFam,
        selectedSites: filterSite,
        selectedStatus: filterStatus,
      });

      //Get the List of PECR to show by default
      fetchList({
        sort: '',
        ascdesc: '',
        filterStatus: filterStatus,
        filterSites: filterSite,
        filterProductFamily: filterProdFam,
        search: searchString,
      });
    }
    //eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    filterProdFamData,
    filterStatusData,
    filterSiteData,
    apiConfig,
    hasURLFilterData,
    filterId,
  ]);

  useEffect(() => {
    //map product family and set checked as true
    if (productFamilyList && productFamilyList.length > 0) {
      const filterData = productFamilyList.map(productFamilyList => {
        return {
          ...productFamilyList,
          checked: true,
        };
      });
      setFilterProdFamData(filterData);
    }
  }, [productFamilyList]);
  //To load the page with bookmarked filter
  useEffect(() => {
    const urlFilterData = getURLFilterData();

    if (
      urlFilterRef.current ||
      !urlFilterData ||
      productFamilyList.length === 0 ||
      statusListData.length === 0 ||
      siteListData.length === 0
    ) {
      return;
    }

    urlFilterRef.current = true;
    setIsLoading(true);

    fetchList({
      sort:
        selectedFilterOption && selectedFilterOption.SortBy
          ? selectedFilterOption.SortBy
          : 'ID',
      ascdesc:
        selectedFilterOption && selectedFilterOption.OrderBy
          ? selectedFilterOption.OrderBy
          : 'desc',
      filterStatus: urlFilterData.statusFilter,
      filterSites: urlFilterData.sitesFilter,
      filterProductFamily: urlFilterData.productFilter,
      search: searchString,
    });

    setSelectedFilterOption(prev => ({
      ...prev,
      ProductFilter: urlFilterData.productFilter,
      SitesFilter: urlFilterData.sitesFilter,
      StatusFilter: urlFilterData.statusFilter,
    }));

    const selectedProdFam =
      urlFilterData.productFilter.length > 0
        ? urlFilterData.productFilter.split(',')
        : [];
    const selectedSites =
      urlFilterData.sitesFilter.length > 0
        ? urlFilterData.sitesFilter.split(',')
        : [];
    const selectedStatus =
      urlFilterData.statusFilter.length > 0
        ? urlFilterData.statusFilter.split(',')
        : [];

    setFilterProdFamData(
      productFamilyList.map(item => ({
        ...item,
        checked: selectedProdFam.includes(item.Name),
      }))
    );

    setFilterSiteData(
      siteListData.map(item => ({
        ...item,
        checked: selectedSites.includes(item.SiteName),
      }))
    );

    setFilterStatusData(
      statusListData.map(item => ({
        ...item,
        checked: selectedStatus.includes(item.Status),
      }))
    );
    //eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    location.search,
    productFamilyList,
    statusListData,
    siteListData,
    apiConfig,
  ]);

  //To load the page with bookmarked filter
  useEffect(() => {
    if (
      bookmarkRef.current ||
      hasURLFilterData ||
      filterId === 'List' ||
      !allFilterData ||
      productFamilyList.length === 0 ||
      statusListData.length === 0 ||
      siteListData.length === 0
    ) {
      return;
    }
    bookmarkRef.current = true;
    setIsLoading(true);
    const findFilter = allFilterData.find(list => {
      return filterId.toString() === list.ID.toString();
    });

    // If filter not found, redirect to default list view
    if (!findFilter) {
      const defaultProdFam = productFamilyList.map(item => ({
        ...item,
        checked: true,
      }));
      const defaultSites = siteListData.map(item => ({
        ...item,
        checked: true,
      }));
      const defaultStatuses = statusListData.map(item => ({
        ...item,
        checked: item.Status !== 'Complete' && item.Status !== 'Cancelled',
      }));

      setFilterProdFamData(defaultProdFam);
      setFilterSiteData(defaultSites);
      setFilterStatusData(defaultStatuses);

      setSelectedFilterOption({
        ProductFilter: filterCheckedItems(defaultProdFam, 'Name'),
        SitesFilter: filterCheckedItems(defaultSites, 'SiteName'),
        StatusFilter: filterCheckedItems(defaultStatuses, 'Status'),
      });

      urlFilterRef.current = true;
      updateURLWithFilters({
        selectedProductFamily: filterCheckedItems(defaultProdFam, 'Name'),
        selectedSites: filterCheckedItems(defaultSites, 'SiteName'),
        selectedStatus: filterCheckedItems(defaultStatuses, 'Status'),
      });

      fetchList({
        sort: '',
        ascdesc: '',
        filterStatus: filterCheckedItems(defaultStatuses, 'Status'),
        filterSites: filterCheckedItems(defaultSites, 'SiteName'),
        filterProductFamily: filterCheckedItems(defaultProdFam, 'Name'),
        search: searchString,
      });
      return;
    }

    setSelectedFilterOption(findFilter);

    const findProdFamFiltered = findFilter.ProductFilter.split(',');
    const findStatusFiltered = findFilter.StatusFilter.split(',');
    const findSiteFiltered = findFilter.SitesFilter.split(',');

    // Get the List of PECR based on selected filter
    fetchList({
      sort: findFilter.SortBy,
      ascdesc: findFilter.OrderBy,
      filterStatus: findFilter.StatusFilter,
      filterSites: findFilter.SitesFilter,
      filterProductFamily: findFilter.ProductFilter,
    });
    const prodFamFiltered = productFamilyList.map(item => {
      if (findProdFamFiltered.includes(item.Name)) {
        return { ...item, checked: true };
      } else {
        return { ...item, checked: false };
      }
    });
    setFilterProdFamData(prodFamFiltered);

    const siteFiltered = siteListData.map(item => {
      if (findSiteFiltered.includes(item.SiteName)) {
        return { ...item, checked: true };
      } else {
        return { ...item, checked: false };
      }
    });
    setFilterSiteData(siteFiltered);

    const statusFiltered = statusListData.map(item => {
      if (findStatusFiltered.includes(item.Status)) {
        return { ...item, checked: true };
      } else {
        return { ...item, checked: false };
      }
    });
    setFilterStatusData(statusFiltered);

    urlFilterRef.current = true;
    updateURLWithFilters({
      selectedProductFamily: findFilter.ProductFilter,
      selectedSites: findFilter.SitesFilter,
      selectedStatus: findFilter.StatusFilter,
    });

    setIsLoading(false);
    //eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    allFilterData,
    filterId,
    productFamilyList,
    statusListData,
    siteListData,
    hasURLFilterData,
    listData,
    apiConfig,
  ]);

  //options for My Save View drop-down
  const saveViewOptions = Array.isArray(allFilterData)
    ? allFilterData.map(list => {
        return {
          label: list.FilterName,
          value: list.ID,
        };
      })
    : [];

  const navigateToTrackingPage = pecrId => {
    const params = new URLSearchParams(location.search);
    params.set('id', pecrId);

    //navigating to tracking page based on pecrId
    navigate({
      pathname: `/TrackingFile`,
      search: `?${params.toString()}`,
    });
  };

  //To handle the refresh button of Filter
  const onRefreshClick = filteredData => {
    // Get the List of PECR based on selected filter
    fetchList({
      filterStatus: filteredData.selectedStatus,
      filterSites: filteredData.selectedSites,
      filterProductFamily: filteredData.selectedProductFamily,
      search: searchString,
      sort:
        selectedFilterOption && selectedFilterOption.SortBy
          ? selectedFilterOption.SortBy
          : 'ID',
      ascdesc:
        selectedFilterOption && selectedFilterOption.OrderBy
          ? selectedFilterOption.OrderBy
          : 'desc',
    });

    updateURLWithFilters(filteredData);

    setSelectedFilterOption({
      ...selectedFilterOption,
      ProductFilter: filteredData.selectedProductFamily,
      SitesFilter: filteredData.selectedSites,
      StatusFilter: filteredData.selectedStatus,
    });
  };

  //To handle the onclick event of Save icon
  const onSaveIconClick = () => {
    setOpenSaveModal(true);
  };

  //To handle the close/cancel click of saved view Name Modal
  const onCloseModalClick = () => {
    setOpenSaveModal(false);
    setErrorMessage(null);
  };

  //To handle the Save click of Saved View Name Modal
  const onSaveModalClick = async () => {
    setErrorMessage(null);
    //Call InsertUpdatePECRFilter API to create new filter with new name and other selected parameters
    if (checkUserInfo && checkUserInfo.userID) {
      await insertNewFilter({
        FilterId: null,
        UserId: checkUserInfo.userID,
        sortBy:
          selectedFilterOption && selectedFilterOption.SortBy
            ? selectedFilterOption.SortBy
            : 'ID',
        orderBy:
          selectedFilterOption && selectedFilterOption.OrderBy
            ? selectedFilterOption.OrderBy
            : 'desc',
        statusFilter: selectedFilterOption.StatusFilter,
        siteFilter: selectedFilterOption.SitesFilter,
        productFilter: selectedFilterOption.ProductFilter,
        filterName: filterName,
        default: 1,
      });
    }
  };

  //handle onchange event of My saved view drop-down
  const handleSavedViewChange = event => {
    const findFilter = allFilterData.find(list => {
      return event.value === list.ID;
    });

    // Safety check to ensure filter exists
    if (!findFilter) {
      console.error(`Filter with ID ${event.value} not found`);
      return;
    }

    setSelectedFilterOption(findFilter);
    const findProdFamFiltered = findFilter.ProductFilter.split(',');
    const findStatusFiltered = findFilter.StatusFilter.split(',');
    const findSiteFiltered = findFilter.SitesFilter.split(',');
    // Get the List of PECR based on selected filter
    fetchList({
      sort: findFilter.SortBy,
      ascdesc: findFilter.OrderBy,
      filterStatus: findFilter.StatusFilter,
      filterSites: findFilter.SitesFilter,
      filterProductFamily: findFilter.ProductFilter,
      // search: findFilter,
    });
    const prodFamFiltered = productFamilyList.map(item => {
      if (findProdFamFiltered.includes(item.Name)) {
        return { ...item, checked: true };
      } else {
        return { ...item, checked: false };
      }
    });
    setFilterProdFamData(prodFamFiltered);

    const siteFiltered = siteListData.map(item => {
      if (findSiteFiltered.includes(item.SiteName)) {
        return { ...item, checked: true };
      } else {
        return { ...item, checked: false };
      }
    });
    setFilterSiteData(siteFiltered);

    const statusFiltered = statusListData.map(item => {
      if (findStatusFiltered.includes(item.Status)) {
        return { ...item, checked: true };
      } else {
        return { ...item, checked: false };
      }
    });
    setFilterStatusData(statusFiltered);

    replaceURL(event.value);

    //updating Saved filter to show the User later as default Filter
    updateUserFilter({
      userId: checkUserInfo.userID,
      filterId: event.value,
    });
  };

  const handleDeleteSavedView = async option => {
    if (!option?.value || !checkUserInfo?.userID) {
      return;
    }

    await deleteSavedFilter({
      filterId: option.value,
      userId: checkUserInfo.userID,
    });

    await queryClient.invalidateQueries({
      queryKey: ['all-filters', { userId: checkUserInfo.userID }],
    });

    if (
      selectedFilterOption?.ID &&
      selectedFilterOption.ID.toString() === option.value.toString()
    ) {
      const currentProductFilter = filterCheckedItems(
        filterProdFamData,
        'Name'
      );
      const currentSiteFilter = filterCheckedItems(filterSiteData, 'SiteName');
      const currentStatusFilter = filterCheckedItems(
        filterStatusData,
        'Status'
      );

      setSelectedFilterOption({
        ProductFilter: currentProductFilter,
        SitesFilter: currentSiteFilter,
        StatusFilter: currentStatusFilter,
      });

      fetchList({
        sort: '',
        ascdesc: '',
        filterStatus: currentStatusFilter,
        filterSites: currentSiteFilter,
        filterProductFamily: currentProductFilter,
        search: searchString,
      });

      replaceURL('List');
      dispatch(userInfo({ filterID: 'List' }));
    }
  };

  const replaceURL = value => {
    navigate(`/List/${value}`, { replace: true });
  };

  //to get the value for My Saved View drop down
  const valueAfterFilterSelection =
    saveViewOptions.length > 0 &&
    selectedFilterOption &&
    !isEmptyObject(selectedFilterOption) &&
    selectedFilterOption.ID &&
    saveViewOptions.find(
      ({ value }) => selectedFilterOption.ID.toString() === value.toString()
    );

  if (errorInAPI.hasErrorOccured) {
    return <ErrorAlert message={errorInAPI.message} />;
  }

  return (
    <div className="flex flex-col">
      <div>
        <PageHeader title={t('PECR_List_Table')}>
          <DownloadList
            selectedFilterOption={selectedFilterOption}
            searchString={searchString}
          />
        </PageHeader>
        <Box style={modernContentSection}>
          {isLoading ? (
            <CircularProgress />
          ) : (
            <Grid style={ListFilterTableGrid}>
              <LeftPanel
                handleSavedViewChange={handleSavedViewChange}
                handleDeleteSavedView={handleDeleteSavedView}
                onRefreshClick={onRefreshClick}
                onSaveIconClick={onSaveIconClick}
                saveViewOptions={saveViewOptions}
                valueAfterFilterSelection={valueAfterFilterSelection}
                filterProdFamData={filterProdFamData}
                filterSiteData={filterSiteData}
                filterStatusData={filterStatusData}
              />
              {listData.length > 0 && (
                <ListTable
                  rows={listData}
                  navigateToTrackingPage={navigateToTrackingPage}
                  showLeftPanel={true}
                />
              )}
              {!listData.length && <EmptyTable isLoading={isPending} />}
            </Grid>
          )}
        </Box>
      </div>

      {allFilterData && (
        <MySavedViewModal
          openSaveModal={openSaveModal}
          onCloseModalClick={onCloseModalClick}
          errorMessage={errorMessage}
          setFilterName={setFilterName}
          getAllFilters={allFilterData || []}
          setErrorMessage={setErrorMessage}
          onSaveModalClick={onSaveModalClick}
          filterName={filterName}
          userId={checkUserInfo.userID}
        />
      )}
    </div>
  );
};

const mapStateToProps = (state = {}) => {
  return {
    checkUserInfo: state.userInfo,
    productFamilyList: state.pecrInfo.pecrData.productFamilyList,
    errorInAPI: state.errorNotification,
  };
};

export default connect(mapStateToProps, null)(ListPage);
